import { Injectable } from '@angular/core';
import { Namespace } from 'enums-lib';
import { IFieldMeta } from '../interfaces/imetadata';
import { FieldContext } from '../utils/fieldcontext';

/*
  Handles the fact that the data model can be different from the pull request model.
  An alternative would be for the server to provide both the field.path (request model) and a field.dataPath (data model).
  Also handles the special list item "status" variables for script fields.
 */
@Injectable({ providedIn: 'root' })
export class DataSourcePathService {
  getFullyQualifiedPath(fieldContext: FieldContext): string {
    return this.getService(fieldContext).getFullyQualifiedPath(fieldContext);
  }

  getOwnerPath(owner: string, fieldContext: FieldContext): string {
    return this.getService(fieldContext).getOwnerPath(owner, fieldContext);
  }

  private getService(fieldContext: FieldContext): DefaultDataSourcePathService {
    // order is important - a script field will be under the namespace

    if (fieldContext.isScriptField) {
      return ScriptFieldDataSourcePathService.getInstance();
    }

    if (fieldContext.isImageField || fieldContext.isAiAssistField) {
      return CustomPathDataSourcePathService.getInstance();
    }

    if (fieldContext.namespace === Namespace.bullhorn) {
      return BullhornDataSourcePathService.getInstance();
    }

    if (fieldContext.namespace === Namespace.salesforce) {
      return SalesforceDataSourcePathService.getInstance();
    }

    return DefaultDataSourcePathService.getInstance();
  }
}

class DefaultDataSourcePathService {
  private static instance: DefaultDataSourcePathService;

  protected constructor() {}

  static getInstance(): DefaultDataSourcePathService {
    if (!DefaultDataSourcePathService.instance) {
      DefaultDataSourcePathService.instance = new DefaultDataSourcePathService();
    }
    return DefaultDataSourcePathService.instance;
  }

  /* would have like to do something clever like this, but could not get it to work without making the ctors public
   static getInstance<T extends DefaultDataSourcePathService>(this: new() => T): T {
    if (!DefaultDataSourcePathService.instance)
      DefaultDataSourcePathService.instance = new this();
    return DefaultDataSourcePathService.instance as T;
   }
  */

  getFullyQualifiedPath(fieldContext: FieldContext): string {
    return `${fieldContext.namespace}.${fieldContext.field.path}`; // use the source field as the path
  }

  getOwnerPath(owner: string, fieldContext: FieldContext): string {
    return `${owner}.${fieldContext.leaf}`;
  }
}

class BullhornDataSourcePathService extends DefaultDataSourcePathService {
  private static bInstance: BullhornDataSourcePathService;

  private constructor() {
    super();
  }

  static override getInstance(): BullhornDataSourcePathService {
    if (!BullhornDataSourcePathService.bInstance) {
      BullhornDataSourcePathService.bInstance = new BullhornDataSourcePathService();
    }
    return BullhornDataSourcePathService.bInstance;
  }

  override getFullyQualifiedPath(fieldContext: FieldContext): string {
    const path = this.makeFullyQualifiedFieldPath(fieldContext);
    return this.processField(path, fieldContext.field);
  }

  override getOwnerPath(owner: string, fieldContext: FieldContext): string {
    const path = this.makeOwnerPath(owner, fieldContext);
    return this.processField(path, fieldContext.field);
  }

  private makeFullyQualifiedFieldPath(fieldContext: FieldContext): string {
    if (fieldContext.isPickerField) {
      return `${fieldContext.namespace}.${this.createPickerFieldPath(fieldContext)}`;
    }

    return super.getFullyQualifiedPath(fieldContext);
  }

  private makeOwnerPath(owner: string, fieldContext: FieldContext): string {
    if (fieldContext.isPickerField) {
      return `${owner}.additionalProperties.${fieldContext.leaf}`;
    }

    return super.getOwnerPath(owner, fieldContext);
  }

  private createPickerFieldPath(fieldContext: FieldContext): string {
    const pathAsArray = fieldContext.field.path.split('.');
    pathAsArray.splice(1, 0, 'additionalProperties');
    return pathAsArray.join().replaceAll(',', '.');
  }

  private processField(path: string, field: IFieldMeta): string {
    return field.type === 'to-many' ? `${path}.data` : path;
  }
}

class SalesforceDataSourcePathService extends DefaultDataSourcePathService {
  private static sInstance: SalesforceDataSourcePathService;

  private constructor() {
    super();
  }

  static override getInstance(): SalesforceDataSourcePathService {
    if (!SalesforceDataSourcePathService.sInstance) {
      SalesforceDataSourcePathService.sInstance = new SalesforceDataSourcePathService();
    }
    return SalesforceDataSourcePathService.sInstance;
  }

  override getFullyQualifiedPath(fieldContext: FieldContext): string {
    return this.processField(fieldContext);
  }

  override getOwnerPath(owner: string, fieldContext: FieldContext): string {
    const escapedLeaf = this.escapeField(fieldContext.leaf);
    return `${owner}.${escapedLeaf}`;
  }

  private escapeField(field: string): string {
    return field.replace(/__/g, '\\_\\_');
  }

  private processField(fieldContext: FieldContext): string {
      const pathParts = fieldContext.field.path.split('.').slice(1);
      const escapedEntity = this.escapeField(fieldContext.entity.entity);
      const escapedPath = pathParts.map((part) => this.escapeField(part)).join('.');

      const basePath = `${fieldContext.namespace}.${escapedEntity}.${escapedPath}`;

      if (fieldContext.field.type === 'to-many') {
        // generate the null-safe path expression
        return `(${basePath} != null and ${basePath}.records != null) ? ${basePath}.records : {}`;
      } else {
        return basePath;
      }
    }

  }

class CustomPathDataSourcePathService extends DefaultDataSourcePathService {
  private static cInstance: CustomPathDataSourcePathService;

  protected constructor() {
    super();
  }

  static override getInstance(): CustomPathDataSourcePathService {
    if (!CustomPathDataSourcePathService.cInstance) {
      CustomPathDataSourcePathService.cInstance = new CustomPathDataSourcePathService();
    }
    return CustomPathDataSourcePathService.cInstance;
  }

  override getFullyQualifiedPath(fieldContext: FieldContext): string {
    // we don't want the namespace (like 'bullhorn') at the start because the field path contains a custom namespace like 'custom' or 'functions'
    return fieldContext.field.path;
  }
}

class ScriptFieldDataSourcePathService extends CustomPathDataSourcePathService {
  private static sInstance: ScriptFieldDataSourcePathService;

  private constructor() {
    super();
  }

  static override getInstance(): ScriptFieldDataSourcePathService {
    if (!ScriptFieldDataSourcePathService.sInstance) {
      ScriptFieldDataSourcePathService.sInstance = new ScriptFieldDataSourcePathService();
    }
    return ScriptFieldDataSourcePathService.sInstance;
  }

  override getOwnerPath(owner: string, fieldContext: FieldContext): string {
    // See: https://www.thymeleaf.org/doc/tutorials/3.1/usingthymeleaf.html#keeping-iteration-status
    // for list of status variables

    if (fieldContext.field.path.endsWith('.[index]')) {
      return `${owner}Stat.index`;
    }

    if (fieldContext.field.path.endsWith('.[count]')) {
      return `${owner}Stat.count`;
    }

    if (fieldContext.field.path.endsWith('.[size]')) {
      return `${owner}Stat.size`;
    }

    if (fieldContext.field.path.endsWith('.[current]')) {
      return `${owner}Stat.current`;
    }

    return super.getOwnerPath(owner, fieldContext);
  }
}
